<template>
    <div
        class="flex w-full flex-col items-start gap-3 md:flex-row md:flex-wrap"
    >
        <div class="flex w-full flex-col md:w-auto">
            <prime-text
                :model-value="enrollment.firstName"
                :placeholder="$t('customerInformation.firstName')"
                class="w-full md:w-auto"
                :pt="{
                    root: {
                        class: showInvalidFirstName
                            ? 'border-2 border-red-500'
                            : ''
                    }
                }"
                :pt-options="{ mergeSections: true, mergeProps: true }"
                @update:model-value="
                    handleUpdateEnrollment('firstName', $event)
                "
            />
            <small
                v-if="showInvalidFirstName"
                id="first-name-help"
                class="text-red-600 pl-2"
            >
                {{ $t('customerInformation.firstNameRequired') }}
            </small>
            <div class="pl-2 text-sm text-gray-400">
                {{ $t('customerInformation.firstName') }}
            </div>
        </div>

        <div class="w-full md:w-auto">
            <prime-text
                :model-value="enrollment.middleName"
                :placeholder="$t('customerInformation.middleNameOptional')"
                class="w-full md:w-auto"
                @update:model-value="
                    handleUpdateEnrollment('middleName', $event)
                "
            />
            <div class="pl-2 text-sm text-gray-400">
                {{ $t('customerInformation.middleNameOptional') }}
            </div>
        </div>

        <div class="flex w-full flex-col md:w-auto">
            <prime-text
                :model-value="enrollment.lastName"
                :placeholder="$t('customerInformation.lastName')"
                class="w-full md:w-auto"
                :pt="{
                    root: {
                        class: showInvalidLastName
                            ? 'border-2 border-red-500'
                            : ''
                    }
                }"
                :pt-options="{ mergeSections: true, mergeProps: true }"
                @update:model-value="handleUpdateEnrollment('lastName', $event)"
            />
            <small
                v-if="showInvalidLastName"
                id="first-name-help"
                class="text-red-600 pl-2"
            >
                {{ $t('customerInformation.twoOrMoreCharactersRequired') }}
            </small>
            <div class="pl-2 text-sm text-gray-400">
                {{ $t('customerInformation.lastName') }}
            </div>
        </div>

        <!-- This is wild, but basically it breaks the flex row only on medium -->
        <div class="hidden h-0 basis-full md:block lg:hidden" />

        <div class="w-full md:w-auto">
            <dropdown-date-input
                :show-errors="showErrors"
                :date="enrollment.dateOfBirth"
                @update:date="
                    payload => handleUpdateEnrollment('dateOfBirth', payload)
                "
            />
            <div class="pl-2 text-sm text-gray-400">
                {{ $t('customerInformation.dateOfBirth') }}
            </div>
        </div>

        <div
            v-if="!isGuest"
            class="flex items-center gap-2 pt-4 md:pl-4"
        >
            <prime-checkbox
                :id="`add_household_member${identifier}`"
                :model-value="enrollment.householdMember"
                :binary="true"
                @update:model-value="
                    handleUpdateEnrollment('householdMember', $event)
                "
            />
            <label :for="`add_household_member${identifier}`">{{
                $t('purchaseContract.saveParticipant')
            }}</label>
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimeCheckbox from 'primevue/checkbox'
import PrimeText from 'primevue/inputtext'
import type { EnrollmentInputNewEnrollment } from '~/composables/enrollment-input'
import DropdownDateInput from '~/components/Input/DropdownDateInput.vue'
import { computed } from 'vue'

const props = defineProps<{
    identifier: string
    enrollment: EnrollmentInputNewEnrollment
    showErrors: boolean
    isGuest: boolean
}>()

const emit = defineEmits<{
    (e: 'update:enrollment', payload: EnrollmentInputNewEnrollment): void
}>()

const showInvalidFirstName = computed(
    () => props.showErrors && props.enrollment.firstName.length < 1
)

const showInvalidLastName = computed(
    () => props.showErrors && props.enrollment.lastName.length < 2
)

function handleUpdateEnrollment<T extends keyof EnrollmentInputNewEnrollment>(
    key: T,
    value: EnrollmentInputNewEnrollment[T]
) {
    emit('update:enrollment', {
        ...props.enrollment,
        [key]: value
    })
}
</script>
